// localStorage-backed chat history, key per user. Solid signal for reactivity.

import { createSignal, createEffect } from "solid-js";
import { getClaims } from "../../lib/auth";
import type { ChatMessage } from "../../lib/types";

function keyFor(sub: string | undefined | null): string {
  return `booking-control:chat:${sub ?? "anon"}`;
}

function load(sub: string | undefined | null): ChatMessage[] {
  try {
    const raw = localStorage.getItem(keyFor(sub));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter(
      (m): m is ChatMessage =>
        m && typeof m.role === "string" && typeof m.content === "string",
    );
  } catch {
    return [];
  }
}

function save(sub: string | undefined | null, msgs: ChatMessage[]) {
  try {
    // Cap at last 200 messages to avoid unbounded growth.
    const trimmed = msgs.slice(-200);
    localStorage.setItem(keyFor(sub), JSON.stringify(trimmed));
  } catch {
    /* ignore quota errors */
  }
}

const [messages, setMessagesInner] = createSignal<ChatMessage[]>(load(getClaims()?.sub));
const [openSignal, setOpen] = createSignal(false);
const [sending, setSending] = createSignal(false);

// Persist when messages change.
createEffect(() => {
  save(getClaims()?.sub, messages());
});

export function chatMessages() {
  return messages();
}

export function chatOpen() {
  return openSignal();
}

export function setChatOpen(v: boolean) {
  setOpen(v);
}

export function chatSending() {
  return sending();
}

export function setChatSending(v: boolean) {
  setSending(v);
}

export function appendMessage(m: ChatMessage) {
  setMessagesInner((prev) => [...prev, { ...m, ts: m.ts ?? Date.now() }]);
}

export function clearChat() {
  setMessagesInner([]);
}

/// Replace last message (e.g. for streaming / placeholder update).
export function replaceLast(m: ChatMessage) {
  setMessagesInner((prev) => {
    if (prev.length === 0) return [{ ...m, ts: m.ts ?? Date.now() }];
    return [...prev.slice(0, -1), { ...m, ts: m.ts ?? Date.now() }];
  });
}
